import {
  UseInfiniteQueryResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  IntegrationContext,
  IntegrationScope,
  IntegrationUser,
} from "../integrations.types";

export type GoogleCreatedBy = {
  user_id: number;
  email: string;
  full_name: string;
};

export type GoogleAuthenticatedUser = {
  id: string;
  email: string;
  name: string;
};

export type GoogleOauthSettings = {
  tokens: {
    access_token: string; // Time in seconds until the access_token expires (usually 3600)
    refresh_token: string; // Used to obtain new access tokens without user interaction; expires in 90 days
    expires_in: number; // seconds
    ext_expires_in: number; // seconds
    scopes: string; // space-separated list of scopes
    token_type: string; // typically "Bearer"
    expires_on: number; // unix timestamp
    id_token: string; // JWT token containing user information
    authenticated_user: GoogleAuthenticatedUser;
  };
  created_on: string; // when the integration was created
};

export type GoogleServiceAccountSettings = {
  service_account: {
    type: "service_account";
    project_id: string;
    private_key_id: string;
    private_key: string;
    client_email: string;
    client_id: string;
    auth_uri: string;
    token_uri: string;
    auth_provider_x509_cert_url: string;
    client_x509_cert_url: string;
    universe_domain: string;
  };
  delegated_account_email: string;
  domain: string;
  file_name: string;
  created_on: string;
};

export type GoogleSaveServiceAccountCredentialsData = {
  file_name: string;
  scope: IntegrationScope;
  type: "service_account";
  project_id: string;
  private_key_id: string;
  private_key: string;
  client_email: string;
  client_id: string;
  auth_uri: string;
  token_uri: string;
  auth_provider_x509_cert_url: string;
  client_x509_cert_url: string;
  universe_domain: string;
  delegated_account_email: string;
  domain: string;
};

export type GoogleSaveServiceAccountCredentialsResult = {
  data: GoogleIntegration;
};

export type GoogleSaveServiceAccountCredentialsFn = (
  values: GoogleSaveServiceAccountCredentialsData
) => Promise<GoogleSaveServiceAccountCredentialsResult>;

export type GoogleSaveOAuth2CredentialsData = {
  code: string;
  state: string;
  scope: IntegrationScope;
};

export type GoogleSaveOAuth2CredentialsResult = {
  data: GoogleIntegration;
};

export type GoogleSaveOAuth2CredentialsFn = (
  values: GoogleSaveOAuth2CredentialsData
) => Promise<GoogleSaveOAuth2CredentialsResult>;

export enum GoogleAuthenticationMethod {
  SERVICE_ACCOUNT = "service_account",
  OAUTH2 = "oauth2",
}

export type GoogleIntegration = {
  settings: GoogleServiceAccountSettings | GoogleOauthSettings;
  options: {
    method: GoogleAuthenticationMethod;
  };
  name: string;
  integration_id: number;
  created_by: GoogleCreatedBy;
};

export type GoogleGetIntegrationQuery = {
  integration_id?: number;
  scope?: string;
};

export type GoogleGetIntegrationResponse = {
  data: GoogleIntegration[];
};

export type GoogleGetIntegrationFn = (
  query?: GoogleGetIntegrationQuery
) => Promise<GoogleGetIntegrationResponse>;

export type GoogleGetAuthUrlResponse = {
  data: {
    url: string;
  };
};

export type GoogleGetAuthUrlFn = () => Promise<GoogleGetAuthUrlResponse>;

/**
 * M365 Graph API
 */

export type GoogleGetUsersQuery = {
  search?: string;
  page?: string;
  page_size?: number;
  context: IntegrationContext;
};

export type GoogleGetUsersResponse = {
  data: IntegrationUser[];
  nextPage: string;
  page: string;
  success: boolean;
};

export type GoogleGetUsersFn = (
  query?: GoogleGetUsersQuery
) => Promise<GoogleGetUsersResponse>;

export type GoogleUseGetUsers = (
  q?: GoogleGetUsersQuery,
  options?: Omit<
    UseQueryOptions<
      GoogleGetUsersResponse,
      Error,
      GoogleGetUsersResponse,
      readonly unknown[]
    >,
    "queryKey" | "queryFn"
  >
) => UseQueryResult<GoogleGetUsersResponse, Error>;

export type GoogleUseGetInfiniteUsers = (
  q: GoogleGetUsersQuery,
  options?: {
    enabled?: boolean;
  }
) => UseInfiniteQueryResult<{
  pages: GoogleGetUsersResponse[];
  pageParams: number[];
}>;
